import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2, ArrowLeft } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Checkbox } from '@/components/ui/checkbox'
import { accountsService } from '@/services/accountsService'
import { Account, AccountViewModel } from '@/api/types'
import { useAuthStore } from '@/store/auth.store'

/**
 * Account Detail/Form Page
 * For creating and editing accounts
 */
export const ChartsOfAccountsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const isEditMode = id !== 'new' && id !== undefined
  const parentAccount = (location.state as any)?.parentAccount as Account | undefined

  const [account, setAccount] = useState<AccountViewModel>({
    accountNature: 0,
    parentAccountId: 0,
    name: '',
    nameAr: '',
    code: '',
    defaultType: '',
    isTransactionable: false,
    branchId: branchId || 0,
    companyId: 0,
  })

  const [allAccounts, setAllAccounts] = useState<Account[]>([]) // All accounts for finding master node
  const [parentAccounts, setParentAccounts] = useState<Account[]>([]) // Master nodes (parentId = 0 or null)
  const [childAccounts, setChildAccounts] = useState<Account[]>([]) // Children of selected Account Nature
  const [accountLevel, setAccountLevel] = useState<number | null>(null) // 1-5; used to auto-set isTransactionable for level 5
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [codeGenerating, setCodeGenerating] = useState(false)

  // Load all accounts first (needed to find master node)
  useEffect(() => {
    if (branchId) {
      loadAllAccounts()
    }
  }, [branchId])

  // Load account data if editing
  useEffect(() => {
    if (isEditMode && branchId && id) {
      loadAccount()
    } else if (!isEditMode && parentAccount) {
      // When coming from tree with selected account
      if (allAccounts.length > 0) {
        // All accounts loaded, process immediately
        handleParentAccountFromTree(parentAccount)
      }
    }
  }, [isEditMode, branchId, id, parentAccount, allAccounts.length])

  // Handle parentAccount when allAccounts becomes available
  useEffect(() => {
    if (!isEditMode && parentAccount && allAccounts.length > 0) {
      handleParentAccountFromTree(parentAccount)
    }
  }, [allAccounts.length, parentAccount, isEditMode])

  // Load child accounts when Account Nature changes or when allAccounts is loaded
  useEffect(() => {
    if (account.accountNature && account.accountNature > 0 && branchId) {
      // Only load if allAccounts is available (for getting all descendants)
      if (allAccounts.length > 0) {
        loadChildAccounts(account.accountNature)
      }
    } else {
      setChildAccounts([])
    }
  }, [account.accountNature, branchId, allAccounts.length])

  // Generate code when both Account Nature and Parent Account are set (for Add New)
  useEffect(() => {
    if (!isEditMode && account.accountNature && account.accountNature > 0 && account.parentAccountId && account.parentAccountId > 0) {
      // Small delay to ensure state is updated
      const timer = setTimeout(() => {
        if (!codeGenerating) {
          generateCode(account.parentAccountId)
        }
      }, 100)
      return () => {
        clearTimeout(timer)
      }
    }
  }, [account.accountNature, account.parentAccountId, isEditMode])

  const loadAllAccounts = async () => {
    if (!branchId) return

    try {
      setLoading(true)
      // Get all accounts
      const response = await accountsService.getAllAccounts(branchId, 1, 1000)
      const allAccountsData = response.data || []
      setAllAccounts(allAccountsData)
      
      // Filter for master nodes (parentId = 0 or null) - these are Account Nature options
      const masterNodes = allAccountsData.filter((acc: Account) => {
        const parentId = acc.parentAccountId || acc.parentId
        return parentId === 0 || parentId === null || parentId === undefined
      })
      
      // Sort by specific order: Liabilities, Assets, Equity, Revenue, Expenses
      // This ensures consistent ordering across all branches
      const accountOrder: { [key: string]: number } = {
        'liabilities': 1,
        'assets': 2,
        'equity': 3,
        'revenue': 4,
        'expenses': 5,
      }

      const getAccountOrder = (account: Account): number => {
        const name = (account.nameEn || account.name || account.nameAr || '').toLowerCase().trim()
        
        // Check for exact matches first
        for (const [key, order] of Object.entries(accountOrder)) {
          if (name === key) {
            return order
          }
        }
        
        // Check for partial matches (e.g., "Current Assets" contains "assets")
        for (const [key, order] of Object.entries(accountOrder)) {
          if (name.includes(key)) {
            return order
          }
        }
        
        // If no match, try to extract from code
        const code = account.code || account.accountCode || ''
        const numFromCode = code ? (parseInt(code) || parseInt(code.match(/\d+/)?.[0] || '0') || 0) : 0
        
        // Map code numbers to order (1=Liabilities, 2=Assets, 3=Equity, 4=Revenue, 7=Expenses)
        if (numFromCode === 1) return 1 // Liabilities
        if (numFromCode === 2) return 2 // Assets
        if (numFromCode === 3) return 3 // Equity
        if (numFromCode === 4) return 4 // Revenue
        if (numFromCode === 7) return 5 // Expenses
        
        // Default: put unmatched accounts at the end
        return 999
      }

      const sortedMasterNodes = masterNodes.sort((a, b) => {
        const orderA = getAccountOrder(a)
        const orderB = getAccountOrder(b)
        
        // If same order, sort by code number
        if (orderA === orderB) {
          const codeA = a.code || a.accountCode || ''
          const codeB = b.code || b.accountCode || ''
          const numA = codeA ? (parseInt(codeA) || parseInt(codeA.match(/\d+/)?.[0] || '0') || 0) : 0
          const numB = codeB ? (parseInt(codeB) || parseInt(codeB.match(/\d+/)?.[0] || '0') || 0) : 0
          return numA - numB
        }
        
        return orderA - orderB
      })
      
      setParentAccounts(sortedMasterNodes)
    } catch (error: any) {
      await showError('Error', error.response?.data?.message || t('chartsOfAccounts.failedToLoadAccounts'))
    } finally {
      setLoading(false)
    }
  }

  // Find master node (root parent) for a given account
  const findMasterNode = (account: Account): Account | null => {
    if (!account) return null
    
    const parentId = account.parentAccountId || account.parentId || 0
    if (parentId === 0 || parentId === null) {
      // This account itself is a master node
      return account
    }
    
    // Find parent account
    const parent = allAccounts.find((acc: Account) => {
      const accId = acc.accountId || acc.id
      return accId === parentId
    })
    
    if (!parent) return null
    
    // Recursively find master node
    return findMasterNode(parent)
  }

  // Handle when parent account is passed from tree
  const handleParentAccountFromTree = async (selectedAccount: Account) => {
    if (allAccounts.length === 0) {
      return
    }
    
    // Find master node (root parent with parentId = 0 or null)
    const masterNode = findMasterNode(selectedAccount)
    
    if (masterNode) {
      const masterNodeId = masterNode.accountId || masterNode.id || 0
      const selectedAccountId = selectedAccount.accountId || selectedAccount.id || 0
      const parentLevel = selectedAccount.level ?? 0
      const newLevel = parentLevel + 1
      setAccountLevel(newLevel)

      // Set Account Nature to master node; Set Parent Account to selected account
      // Level 5 accounts are always transactionable
      const updatedAccount = {
        accountNature: masterNodeId,
        parentAccountId: selectedAccountId,
        isTransactionable: newLevel === 5 ? true : false,
      }
      setAccount((prev) => ({
        ...prev,
        ...updatedAccount,
      }))
      
      // Load children of master node for Parent Account dropdown
      if (masterNodeId > 0 && branchId) {
        await loadChildAccounts(masterNodeId)
      }
      
      // Generate code immediately after setting parentAccountId
      if (selectedAccountId > 0) {
        setTimeout(() => {
          generateCode(selectedAccountId)
        }, 200) // Small delay to ensure state is updated
      }
    } else {
      // Fallback: if selected account itself is a root, use it
      const selectedAccountId = selectedAccount.accountId || selectedAccount.id || 0
      const parentId = selectedAccount.parentAccountId || selectedAccount.parentId || 0
      const parentLevel = selectedAccount.level ?? 0
      const newLevel = parentLevel + 1
      setAccountLevel(newLevel)

      if (parentId === 0 || parentId === null) {
        // Selected account is itself a root (new account will be level 2)
        setAccount((prev) => ({
          ...prev,
          accountNature: selectedAccountId,
          parentAccountId: selectedAccountId,
          isTransactionable: newLevel === 5 ? true : prev.isTransactionable,
        }))

        if (selectedAccountId > 0) {
          setTimeout(() => {
            generateCode(selectedAccountId)
          }, 200)
        }
      } else {
        setAccount((prev) => ({
          ...prev,
          accountNature: selectedAccount.accountNature || 0,
          parentAccountId: selectedAccountId,
          isTransactionable: newLevel === 5 ? true : prev.isTransactionable,
        }))

        if (selectedAccountId > 0) {
          setTimeout(() => {
            generateCode(selectedAccountId)
          }, 200)
        }
      }
    }
  }

  // Recursively get all descendants of a given account ID (excluding level 5+ accounts/GL files)
  const getAllDescendants = (parentId: number, accounts: Account[]): Account[] => {
    const descendants: Account[] = []
    
    // Find all direct children (exclude level 5+ accounts)
    const directChildren = accounts.filter((acc: Account) => {
      const accParentId = acc.parentAccountId || acc.parentId || 0
      const accLevel = acc.level || 0
      // Only include accounts up to level 4, exclude level 5+ (GL files)
      return accParentId === parentId && accLevel < 5
    })
    
    // Add direct children to descendants
    descendants.push(...directChildren)
    
    // Recursively get descendants of each child (only if child is not level 5+)
    directChildren.forEach((child: Account) => {
      const childId = child.accountId || child.id || 0
      const childLevel = child.level || 0
      // Only recurse if child is not level 5+ (GL files)
      if (childId > 0 && childLevel < 5) {
        const childDescendants = getAllDescendants(childId, accounts)
        descendants.push(...childDescendants)
      }
    })
    
    return descendants
  }

  const loadChildAccounts = async (parentId: number) => {
    if (!branchId) return

    try {
      // If we have allAccounts loaded, use them to get all descendants
      if (allAccounts.length > 0) {
        // Find the root node itself (Account Nature)
        const rootNode = allAccounts.find((acc: Account) => {
          const accId = acc.accountId || acc.id || 0
          return accId === parentId
        })
        
        // Get all descendants of the root node
        const allDescendants = getAllDescendants(parentId, allAccounts)
        
        // Include root node itself in the list (so user can select it as parent)
        const accountsList: Account[] = []
        if (rootNode) {
          accountsList.push(rootNode)
        }
        
        // Add all descendants
        accountsList.push(...allDescendants)
        
        setChildAccounts(accountsList)
      } else {
        // Fallback: load direct children only (shouldn't happen if allAccounts is loaded)
        const children = await accountsService.getAccountsByParentId(parentId, branchId)
        
        // Also try to get the root node itself
        try {
          const rootNode = await accountsService.getAccountById(branchId, parentId)
          const accountsList = [rootNode, ...children]
          setChildAccounts(accountsList)
        } catch (e) {
          setChildAccounts(children)
        }
      }
    } catch (error: any) {
      setChildAccounts([])
    }
  }

  const loadAccount = async () => {
    if (!branchId || !id) return

    try {
      setLoading(true)
      const accountData = await accountsService.getAccountById(branchId, Number(id))
      
      const parentAccountId = accountData.parentAccountId || accountData.parentId || 0
      let accountNature = accountData.accountNature || 0
      
      // If parentAccountId exists but accountNature is 0, find root node from parent
      if (parentAccountId > 0 && accountNature === 0) {
        // Load parent account
        try {
          const parentAccount = await accountsService.getAccountById(branchId, parentAccountId)
          
          // Find root node (accountNature) by traversing up the parent chain
          if (allAccounts.length > 0) {
            const rootNode = findMasterNode(parentAccount)
            if (rootNode) {
              accountNature = rootNode.accountId || rootNode.id || 0
            } else {
              // If parent itself is a root node
              const parentParentId = parentAccount.parentAccountId || parentAccount.parentId || 0
              if (parentParentId === 0) {
                accountNature = parentAccount.accountId || parentAccount.id || 0
              }
            }
          } else {
            // If allAccounts not loaded yet, try to find from parent's parentAccountId
            const parentParentId = parentAccount.parentAccountId || parentAccount.parentId || 0
            if (parentParentId === 0) {
              // Parent is root node
              accountNature = parentAccount.accountId || parentAccount.id || 0
            } else {
              // Load parent's parent recursively until we find root
              let currentParent = parentAccount
              let maxDepth = 10 // Prevent infinite loop
              while (maxDepth > 0 && currentParent) {
                const currentParentId = currentParent.parentAccountId || currentParent.parentId || 0
                if (currentParentId === 0) {
                  accountNature = currentParent.accountId || currentParent.id || 0
                  break
                }
                try {
                  currentParent = await accountsService.getAccountById(branchId, currentParentId)
                  maxDepth--
                } catch (e) {
                  break
                }
              }
            }
          }
        } catch (error: any) {
          // Continue with accountNature = 0
        }
      }
      
      const level = accountData.level ?? 0
      setAccountLevel(level)
      // Level 5 accounts are always transactionable
      const isTransactionable = level === 5 ? true : (accountData.isTransactionable || false)

      setAccount({
        accountId: accountData.accountId || accountData.id,
        accountNature: accountNature,
        parentAccountId: parentAccountId,
        name: accountData.name || accountData.nameEn || accountData.accountNameEn || '',
        nameAr: accountData.nameAr || accountData.accountNameAr || '',
        code: accountData.code || accountData.accountCode || '',
        defaultType: accountData.defaultType || '',
        isTransactionable,
        branchId: branchId,
        companyId: accountData.companyId || 0,
      })

      // Load child accounts for the account nature
      if (accountNature > 0) {
        await loadChildAccounts(accountNature)
      }
    } catch (error: any) {
      await showError('Error', error.response?.data?.message || t('chartsOfAccounts.failedToLoadAccount'))
    } finally {
      setLoading(false)
    }
  }

  const generateCode = async (parentId?: number) => {
    const parentAccountId = parentId || account.parentAccountId || 0
    
    if (!parentAccountId || parentAccountId === 0) {
      return
    }

    try {
      setCodeGenerating(true)
      
      const code = await accountsService.generateAccountCode(parentAccountId)
      
      if (code && code.trim() !== '') {
        setAccount((prev) => {
          const updated = { ...prev, code }
          return updated
        })
      } else {
        await showError('Error', t('chartsOfAccounts.failedToGenerateAccountCode'))
      }
    } catch (error: any) {
      await showError('Error', error.response?.data?.message || t('chartsOfAccounts.failedToGenerateAccountCode'))
    } finally {
      setCodeGenerating(false)
    }
  }

  const handleAccountNatureChange = (value: string) => {
    const natureId = Number(value) || 0
    setAccountLevel(null)
    setAccount((prev) => ({
      ...prev,
      accountNature: natureId,
      parentAccountId: 0, // Reset parent when nature changes
    }))
  }

  const handleParentAccountChange = (value: string) => {
    const parentId = Number(value) || 0
    // Compute new account level: parent level + 1. Level 5 => auto isTransactionable true
    const parent = childAccounts.find(
      (acc) => (acc.accountId ?? acc.id) === parentId
    ) || allAccounts.find((acc) => (acc.accountId ?? acc.id) === parentId)
    const parentLevel = parent?.level ?? 0
    const newLevel = parentLevel + 1
    setAccountLevel(newLevel)

    setAccount((prev) => {
      const updated = {
        ...prev,
        parentAccountId: parentId,
        code: '', // Clear code when parent changes, will be regenerated
        // Level 5 accounts are always transactionable
        isTransactionable: newLevel === 5 ? true : prev.isTransactionable,
      }
      return updated
    })
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('chartsOfAccounts.pleaseSelectBranchFirst'))
      return
    }

    if (!account.accountNature || account.accountNature === 0) {
      await showValidationError(t('chartsOfAccounts.pleaseSelectAccountNature'))
      return
    }

    if (!account.name || account.name.trim() === '') {
      await showValidationError(t('chartsOfAccounts.pleaseEnterAccountName'))
      return
    }

    if (!account.code || account.code.trim() === '') {
      await showValidationError(t('chartsOfAccounts.accountCodeRequired'))
      return
    }

    if (!account.defaultType) {
      await showValidationError(t('chartsOfAccounts.pleaseSelectDefaultType'))
      return
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? t('chartsOfAccounts.updatingAccount') : t('chartsOfAccounts.savingAccount'),
        t('chartsOfAccounts.pleaseWait')
      )
      
      // Level 5 accounts are always transactionable
      const isTransactionable = accountLevel === 5 ? true : (account.isTransactionable || false)
      const payload: AccountViewModel = {
        ...account,
        branchId,
        isTransactionable,
      }

      if (isEditMode) {
        await accountsService.updateAccount(payload)
      } else {
        await accountsService.saveAccount(payload)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('chartsOfAccounts.accountUpdatedSuccess') : t('chartsOfAccounts.accountCreatedSuccess')
      )

      navigate('/charts-of-accounts')
    } catch (error: any) {
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  // Prepare dropdown options
  // Account Nature: Only show master nodes (parentId = 0 or null), sorted by code number
  const accountNatureOptions: SearchableSelectOption[] = parentAccounts
    .map((acc) => ({
      value: String(acc.accountId || acc.id || 0),
      label: `${acc.nameEn || acc.name || acc.nameAr || 'Unnamed'} ${acc.code || acc.accountCode ? `- ${acc.code || acc.accountCode}` : ''}`,
      sortKey: parseInt((acc.code || acc.accountCode || '').match(/\d+/)?.[0] || '0') || 0,
    }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(({ sortKey, ...option }) => option) // Remove sortKey from final options

  // Ensure current parent account is included in options when editing
  const parentAccountOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [
      { value: '0', label: t('chartsOfAccounts.none') },
      ...childAccounts.map((acc) => ({
        value: String(acc.accountId || acc.id || 0),
        label: `${acc.nameEn || acc.name || acc.nameAr || 'Unnamed'} ${acc.code ? `- ${acc.code}` : ''}`,
      })),
    ]

    // In edit mode, ensure current parent account is in the options
    if (isEditMode && account.parentAccountId && account.parentAccountId > 0) {
      const parentAccountIdStr = String(account.parentAccountId)
      const parentAccountExists = options.some(opt => opt.value === parentAccountIdStr)
      
      if (!parentAccountExists) {
        // Find the parent account from allAccounts
        const parentAccount = allAccounts.find((acc: Account) => {
          const accId = acc.accountId || acc.id || 0
          return accId === account.parentAccountId
        })
        
        if (parentAccount) {
          // Add parent account to options
          options.push({
            value: parentAccountIdStr,
            label: `${parentAccount.nameEn || parentAccount.name || parentAccount.nameAr || 'Unnamed'} ${parentAccount.code ? `- ${parentAccount.code}` : ''}`,
          })
        }
      }
    }

    return options
  }, [childAccounts, account.parentAccountId, isEditMode, allAccounts])

  const defaultTypeOptions: SearchableSelectOption[] = [
    { value: 'Credit', label: t('chartsOfAccounts.credit') },
    { value: 'Debit', label: t('chartsOfAccounts.debit') },
  ]

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title={t('chartsOfAccounts.account')}
          description={t('chartsOfAccounts.diagramForChartOfAccounts')}
        />
        <Button variant="outline" onClick={() => navigate('/charts-of-accounts')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('chartsOfAccounts.return')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('chartsOfAccounts.accountInfo')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>{t('chartsOfAccounts.accountNature')} <span className="text-red-500">*</span></Label>
              <SearchableSelect
                options={accountNatureOptions}
                value={account.accountNature ? String(account.accountNature) : ''}
                onValueChange={handleAccountNatureChange}
                placeholder={t('chartsOfAccounts.selectAccountNature')}
                searchPlaceholder={t('chartsOfAccounts.searchAccountNatures')}
                emptyMessage={t('chartsOfAccounts.noAccountNaturesFound')}
                disabled={true}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('chartsOfAccounts.parentAccount')}</Label>
              <SearchableSelect
                options={parentAccountOptions}
                value={account.parentAccountId ? String(account.parentAccountId) : '0'}
                onValueChange={handleParentAccountChange}
                placeholder={t('chartsOfAccounts.selectParentAccount')}
                searchPlaceholder={t('chartsOfAccounts.searchParentAccounts')}
                emptyMessage={t('chartsOfAccounts.noParentAccountsFound')}
                disabled={accountLevel !== 5 || saving}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('chartsOfAccounts.name')} <span className="text-red-500">*</span></Label>
              <Input
                value={account.name || ''}
                onChange={(e) => setAccount((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={t('chartsOfAccounts.enterAccountName')}
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('chartsOfAccounts.nameArabic')}</Label>
              <Input
                value={account.nameAr || ''}
                onChange={(e) => setAccount((prev) => ({ ...prev, nameAr: e.target.value }))}
                placeholder={t('chartsOfAccounts.enterAccountNameInArabic')}
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('chartsOfAccounts.code')} <span className="text-red-500">*</span></Label>
              <div className="flex items-center gap-2">
                <Input
                  value={account.code || ''}
                  readOnly
                  className="bg-muted"
                  placeholder={t('chartsOfAccounts.autoGenerated')}
                />
                {codeGenerating && (
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('chartsOfAccounts.defaultType')} <span className="text-red-500">*</span></Label>
              <SearchableSelect
                options={defaultTypeOptions}
                value={account.defaultType || ''}
                onValueChange={(value) => setAccount((prev) => ({ ...prev, defaultType: value }))}
                placeholder={t('chartsOfAccounts.selectDefaultType')}
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('chartsOfAccounts.isTransactionable')}</Label>
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="isTransactionable"
                  checked={accountLevel === 5 ? true : (account.isTransactionable || false)}
                  onCheckedChange={(checked) =>
                    accountLevel !== 5 && setAccount((prev) => ({ ...prev, isTransactionable: checked === true }))
                  }
                  disabled={saving || accountLevel === 5}
                />
                <Label htmlFor="isTransactionable" className={accountLevel === 5 ? '' : 'cursor-pointer'}>
                  {accountLevel === 5 ? t('chartsOfAccounts.yes') : (account.isTransactionable ? t('chartsOfAccounts.yes') : t('chartsOfAccounts.no'))}
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button variant="outline" onClick={() => navigate('/charts-of-accounts')} className="w-full sm:w-auto">
          {t('chartsOfAccounts.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving || !branchId} className="w-full sm:w-auto min-w-[150px]">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('chartsOfAccounts.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('chartsOfAccounts.updateAccount') : t('chartsOfAccounts.saveAccount')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

